import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Container,
  Paper,
  Typography,
  Stack,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  Button,
  Tooltip,
  IconButton,
  useTheme,
  alpha,
  Link,
  Skeleton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  KeyboardVoice as KeyboardVoiceIcon,
  Analytics as AnalyticsIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Chat as ChatIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Alarm as AlarmIcon,
  Event as EventIcon,
  Pending as PendingIcon,
  WatchLater as WatchLaterIcon,
  Cancel as CancelIcon,
  AddTask as AddTaskIcon,
  ErrorOutline as ErrorOutlineIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Feedback as FeedbackIcon,
  Circle as CircleIcon,
  Person as PersonIcon,
  SupportAgent as SupportAgentIcon,
  Schedule as ScheduleIcon,
  CalendarMonth as CalendarMonthIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';

import AudioPlayer from '../../../components/common/AudioPlayer';
import ScoreGauge from '../../../components/common/ScoreGauge';
import TagListComponent from '../../../components/common/TagListComponent';
import ActionButton from '../../../components/common/ActionButton';
import CreateNewTask from '../../../components/common/CreateNewTask';
import { useCall } from '../../../contexts/CallContext';

const cardSx = (theme) => ({
  p: 2.5,
  borderRadius: 3,
  border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
  bgcolor: theme.palette.background.paper,
  height: '100%',
  transition: 'box-shadow 0.25s ease, border-color 0.25s ease',
  '&:hover': {
    boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.06)}`,
    borderColor: alpha(theme.palette.primary.main, 0.18),
  },
});

const SectionHeader = ({ icon, title, action, theme }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
    <Stack direction="row" alignItems="center" spacing={1.25}>
      <Box sx={{
        width: 32,
        height: 32,
        borderRadius: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: alpha(theme.palette.primary.main, 0.08),
        color: theme.palette.primary.main,
      }}>
        {icon}
      </Box>
      <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '0.95rem' }}>
        {title}
      </Typography>
    </Stack>
    {action}
  </Stack>
);

const MetaItem = ({ label, children }) => (
  <Stack spacing={0.5}>
    <Typography variant="caption" sx={{
      opacity: 0.65,
      fontSize: '0.68rem',
      textTransform: 'uppercase',
      letterSpacing: 0.8,
      fontWeight: 600,
    }}>
      {label}
    </Typography>
    {children}
  </Stack>
);

const statusConfig = {
  pending: { bg: '#ffc107', label: 'Pending', icon: <PendingIcon sx={{ fontSize: 16, color: '#ffc107' }} /> },
  in_progress: { bg: '#007bff', label: 'In Progress', icon: <WatchLaterIcon sx={{ fontSize: 16, color: '#007bff' }} /> },
  completed: { bg: '#28a745', label: 'Completed', icon: <CheckIcon sx={{ fontSize: 16, color: '#28a745' }} /> },
  cancelled: { bg: '#6c757d', label: 'Cancelled', icon: <CancelIcon sx={{ fontSize: 16, color: '#6c757d' }} /> },
  overdue: { bg: '#dc3545', label: 'Overdue', icon: <AlarmIcon sx={{ fontSize: 16, color: '#dc3545' }} /> },
};

const CallInsights = () => {
  const { callId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [openTaskModal, setOpenTaskModal] = useState(false);
  const { fetchCallData, callData } = useCall();

  useEffect(() => {
    if (!callData) {
      fetchCallData(callId);
    }
  }, [callId, fetchCallData]);

  if (!callData) {
    return (
      <Box sx={{ py: 4 }}>
        <Container maxWidth="xl">
          <Stack spacing={3}>
            <Skeleton variant="rounded" height={72} sx={{ borderRadius: 3 }} />
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={5}><Skeleton variant="rounded" height={240} sx={{ borderRadius: 3 }} /></Grid>
              <Grid item xs={12} md={7}><Skeleton variant="rounded" height={240} sx={{ borderRadius: 3 }} /></Grid>
            </Grid>
            <Skeleton variant="rounded" height={56} sx={{ borderRadius: 3 }} />
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={5}><Skeleton variant="rounded" height={200} sx={{ borderRadius: 3 }} /></Grid>
              <Grid item xs={12} md={7}><Skeleton variant="rounded" height={200} sx={{ borderRadius: 3 }} /></Grid>
            </Grid>
          </Stack>
        </Container>
      </Box>
    );
  }

  const generalInsights = callData?.general_insights || {};
  const customerInsights = callData?.customer_insights || {};
  const leadScore = generalInsights?.lead_score || 0;
  const scoreValue = leadScore < 10 ? leadScore * 10 : leadScore;
  const speakerRatio = generalInsights?.speaker_ratio || { agent: 50, customer: 50 };
  const agentTasks = callData?.agent_tasks || [];
  const leadScoreJustification = generalInsights?.lead_score_justification || [];
  const topicsDiscussed = generalInsights?.topics_discussed || [];

  const formattedTasks = agentTasks.map(task => {
    const dateObj = parseISO(task.date_time);
    return {
      title: task.title,
      createdBy: task.created_by,
      date: format(dateObj, 'EEE MMM dd'),
      time: format(dateObj, 'hh:mm a'),
      status: task.status || 'overdue',
      status_verbose: task.status_verbose,
    };
  });

  const customerSections = [
    { id: 'queries', title: 'Lead Queries', icon: <QuestionAnswerIcon fontSize="small" />, data: customerInsights.queries || [], color: 'primary', type: 'list' },
    { id: 'concerns', title: 'Lead Concerns', icon: <ErrorOutlineIcon fontSize="small" />, data: customerInsights.concerns || [], color: 'error', type: 'chip' },
    { id: 'preferences', title: 'Lead Preferences', icon: <FavoriteBorderIcon fontSize="small" />, data: customerInsights.preferences || [], color: 'success', type: 'chip' },
    { id: 'feedback', title: 'Lead Feedback', icon: <FeedbackIcon fontSize="small" />, data: customerInsights.feedback || [], color: 'info', type: 'list' },
  ];

  return (
    <Box sx={{ py: 3, bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
      <Container maxWidth="xl">
        <Stack spacing={3}>

          {/* Back + Title */}
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <IconButton
              onClick={() => navigate(-1)}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.16) },
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.15rem' }}>
              Call Details
            </Typography>
          </Stack>

          {/* ── Call Meta Bar ────────────────────────── */}
          <Paper
            elevation={0}
            sx={{
              px: { xs: 2.5, md: 4 },
              py: 2.5,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.primary.light} 100%)`,
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: -40,
                right: -40,
                width: 160,
                height: 160,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.06)',
              },
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={6} sm={3}>
                <MetaItem label="Lead">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PersonIcon sx={{ fontSize: 18, opacity: 0.7 }} />
                    {callData?.lead ? (
                      <Link
                        component="button"
                        onClick={() => navigate(`/leads/${callData.lead.id}`)}
                        sx={{ color: 'white', fontWeight: 600, fontSize: '0.95rem', textDecoration: 'none', textAlign: 'left', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {callData.lead.name}
                      </Link>
                    ) : (
                      <Typography variant="body2" sx={{ color: alpha('#fff', 0.45), fontWeight: 500 }}>
                        Not Assigned
                      </Typography>
                    )}
                  </Stack>
                </MetaItem>
              </Grid>
              <Grid item xs={6} sm={3}>
                <MetaItem label="Agent">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <SupportAgentIcon sx={{ fontSize: 18, opacity: 0.7 }} />
                    <Box>
                      <Link
                        component="button"
                        onClick={() => navigate(`/agents/${callData?.agent?.id}`)}
                        sx={{ color: 'white', fontWeight: 600, fontSize: '0.95rem', textDecoration: 'none', display: 'block', textAlign: 'left', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {callData.agent?.name}{callData.agent?.employee_id ? ` (${callData.agent.employee_id})` : ''}
                      </Link>
                      {callData.agent?.team && (
                        <Typography variant="caption" sx={{ opacity: 0.55, fontSize: '0.68rem' }}>
                          {callData.agent.team}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </MetaItem>
              </Grid>
              <Grid item xs={6} sm={3}>
                <MetaItem label="Duration">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <ScheduleIcon sx={{ fontSize: 18, opacity: 0.7 }} />
                    <Typography variant="body1" fontWeight={600}>
                      {callData.recording_length_formatted}
                    </Typography>
                  </Stack>
                </MetaItem>
              </Grid>
              <Grid item xs={6} sm={3}>
                <MetaItem label="Date">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CalendarMonthIcon sx={{ fontSize: 18, opacity: 0.7 }} />
                    <Typography variant="body1" fontWeight={600}>
                      {callData.date}
                    </Typography>
                  </Stack>
                </MetaItem>
              </Grid>
            </Grid>
          </Paper>

          {/* ── Audio Player  +  Call Summary ────────── */}
          <Grid container spacing={2.5}>
            {/* Left: Recording + Speaker Ratio */}
            <Grid item xs={12} md={5}>
              <Stack spacing={2.5} sx={{ height: '100%' }}>
                <Paper elevation={0} sx={cardSx(theme)}>
                  <SectionHeader icon={<KeyboardVoiceIcon fontSize="small" />} title="Call Recording" theme={theme} />
                  <AudioPlayer audioUrl={callData.recording_url} duration={callData.recording_length_formatted} />
                </Paper>

                <Paper elevation={0} sx={cardSx(theme)}>
                  <SectionHeader icon={<AnalyticsIcon fontSize="small" />} title="Speaker Analysis" theme={theme} />
                  <Box sx={{ height: 8, bgcolor: alpha(theme.palette.divider, 0.25), borderRadius: 4, overflow: 'hidden', display: 'flex', mb: 2 }}>
                    <Box sx={{ width: `${speakerRatio.agent}%`, bgcolor: theme.palette.primary.main, borderRadius: '4px 0 0 4px', transition: 'width 1s ease' }} />
                    <Box sx={{ width: `${speakerRatio.customer}%`, bgcolor: theme.palette.secondary.main, borderRadius: '0 4px 4px 0', transition: 'width 1s ease' }} />
                  </Box>
                  <Stack direction="row" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: theme.palette.primary.main }} />
                      <Typography variant="body2" color="text.secondary" fontSize="0.82rem">
                        Agent: <strong>{speakerRatio.agent}%</strong>
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: theme.palette.secondary.main }} />
                      <Typography variant="body2" color="text.secondary" fontSize="0.82rem">
                        Lead: <strong>{speakerRatio.customer}%</strong>
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              </Stack>
            </Grid>

            {/* Right: Call Summary */}
            <Grid item xs={12} md={7}>
              <Paper elevation={0} sx={{ ...cardSx(theme), display: 'flex', flexDirection: 'column' }}>
                <SectionHeader icon={<QuestionAnswerIcon fontSize="small" />} title="Call Summary" theme={theme} />
                <Box sx={{
                  flex: 1,
                  overflowY: 'auto',
                  maxHeight: 340,
                  pr: 1,
                  '&::-webkit-scrollbar': { width: 4 },
                  '&::-webkit-scrollbar-track': { bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2 },
                  '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.1)', borderRadius: 2 },
                }}>
                  {generalInsights.summary ? (
                    generalInsights.summary.split(/\n\n|\n/).filter(Boolean).map((p, i) => (
                      <Typography key={i} variant="body2" sx={{ color: 'text.primary', lineHeight: 1.8, mb: 1.5, fontSize: '0.9rem', '&:last-child': { mb: 0 } }}>
                        {p}
                      </Typography>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                      No summary available for this call.
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* ── Topics Discussed ─────────────────────── */}
          {topicsDiscussed.length > 0 && (
            <Paper elevation={0} sx={cardSx(theme)}>
              <SectionHeader icon={<ChatIcon fontSize="small" />} title="Topics Discussed" theme={theme} />
              <TagListComponent tags={topicsDiscussed} color="#009688" />
            </Paper>
          )}

          {/* ── Call Score  +  Tasks & Meetings ──────── */}
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={5}>
              <Paper elevation={0} sx={cardSx(theme)}>
                <SectionHeader icon={<AnalyticsIcon fontSize="small" />} title="Call Score" theme={theme} />
                <Stack direction="row" spacing={3} alignItems="flex-start">
                  <Box sx={{ flexShrink: 0 }}>
                    <ScoreGauge
                      value={scoreValue}
                      max={100}
                      size={85}
                      thickness={7}
                      color={scoreValue >= 70 ? 'success' : scoreValue >= 40 ? 'warning' : 'error'}
                      labelFontSize={20}
                      withShadow
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1, fontSize: '0.8rem', color: 'text.secondary' }}>
                      Score Factors
                    </Typography>
                    <List dense disablePadding sx={{
                      maxHeight: 170,
                      overflowY: 'auto',
                      '&::-webkit-scrollbar': { width: 3 },
                      '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.1)', borderRadius: 3 },
                    }}>
                      {leadScoreJustification.length > 0 ? (
                        leadScoreJustification.map((item, i) => {
                          const isNegative = item.factor === true;
                          return (
                            <ListItem key={i} dense disablePadding sx={{ py: 0.35, alignItems: 'flex-start' }}>
                              <ListItemIcon sx={{ minWidth: 20, mt: 0.3 }}>
                                {isNegative
                                  ? <CloseIcon color="error" sx={{ fontSize: 14 }} />
                                  : <CheckIcon color="success" sx={{ fontSize: 14 }} />}
                              </ListItemIcon>
                              <Typography
                                variant="body2"
                                fontSize="0.78rem"
                                sx={{ color: isNegative ? 'error.main' : 'text.primary', lineHeight: 1.45, wordBreak: 'break-word' }}
                              >
                                {typeof item === 'string' ? item : item.value}
                              </Typography>
                            </ListItem>
                          );
                        })
                      ) : (
                        <Typography variant="body2" fontSize="0.78rem" color="text.secondary" fontStyle="italic">
                          No score factors available
                        </Typography>
                      )}
                    </List>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} md={7}>
              <Paper elevation={0} sx={cardSx(theme)}>
                <SectionHeader
                  icon={<EventIcon fontSize="small" />}
                  title="Tasks & Meetings"
                  theme={theme}
                  action={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Tooltip title="Create new task" arrow>
                        <ActionButton onClick={() => setOpenTaskModal(true)}>
                          <AddTaskIcon sx={{ fontSize: '1.1rem', color: theme.palette.warning.main }} />
                        </ActionButton>
                      </Tooltip>
                      <Button
                        variant="text"
                        size="small"
                        component={RouterLink}
                        to="/calendar"
                        sx={{ fontSize: '0.72rem', p: '2px 8px', minHeight: 0, color: theme.palette.primary.main }}
                        endIcon={<EventIcon sx={{ fontSize: '0.85rem' }} />}
                      >
                        View All
                      </Button>
                    </Stack>
                  }
                />
                {formattedTasks.length > 0 ? (
                  <List disablePadding sx={{
                    maxHeight: 210,
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': { width: 3 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.1)', borderRadius: 3 },
                  }}>
                    {formattedTasks.map((task, i) => {
                      const cfg = statusConfig[task.status] || statusConfig.overdue;
                      return (
                        <ListItem
                          key={i}
                          disablePadding
                          disableGutters
                          sx={{
                            mb: 0.75,
                            display: 'block',
                            bgcolor: alpha(cfg.bg, 0.1),
                            borderRadius: 2,
                            borderLeft: `3px solid ${cfg.bg}`,
                            ...(task.status === 'cancelled' && { opacity: 0.6 }),
                          }}
                        >
                          <Box sx={{ p: 1.25 }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
                                {cfg.icon}
                                <Typography
                                  variant="body2"
                                  fontWeight={500}
                                  sx={{
                                    fontSize: '0.82rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    textDecoration: ['completed', 'cancelled'].includes(task.status) ? 'line-through' : 'none',
                                  }}
                                >
                                  {task.title}
                                </Typography>
                              </Stack>
                              <Chip
                                label={task.status_verbose || cfg.label}
                                size="small"
                                sx={{ height: 20, fontSize: '0.625rem', bgcolor: cfg.bg, color: 'white', borderRadius: 0.75, ml: 1 }}
                              />
                            </Stack>
                            <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.25, ml: 3.5 }}>
                              <Typography variant="caption" color="text.secondary" fontSize="0.68rem">
                                {task.date} · {task.time}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" fontSize="0.68rem">
                                by {task.createdBy}
                              </Typography>
                            </Stack>
                          </Box>
                        </ListItem>
                      );
                    })}
                  </List>
                ) : (
                  <Stack alignItems="center" spacing={1} sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary" fontSize="0.82rem">
                      No tasks or meetings scheduled
                    </Typography>
                    <Button
                      onClick={() => setOpenTaskModal(true)}
                      variant="outlined"
                      startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                      size="small"
                      sx={{ fontSize: '0.75rem' }}
                    >
                      Add Task
                    </Button>
                  </Stack>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* ── Lead Insights ────────────────────────── */}
          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, fontSize: '1.05rem' }}>
              Lead Insights
            </Typography>
            <Grid container spacing={2}>
              {customerSections.map((section) => {
                const hasData = section.data.length > 0;
                return (
                  <Grid item xs={12} sm={6} key={section.id}>
                    <Paper
                      elevation={0}
                      sx={{
                        ...cardSx(theme),
                        borderLeft: `3px solid ${theme.palette[section.color].main}`,
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box sx={{ color: `${section.color}.main`, display: 'flex' }}>
                            {section.icon}
                          </Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {section.title}
                          </Typography>
                        </Stack>
                        {hasData && (
                          <Chip
                            label={section.data.length}
                            size="small"
                            color={section.color}
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Stack>

                      {section.type === 'list' ? (
                        hasData ? (
                          <List dense disablePadding>
                            {section.data.map((item, i) => (
                              <ListItem
                                key={i}
                                sx={{
                                  px: 0,
                                  py: 0.6,
                                  borderBottom: i < section.data.length - 1 ? `1px solid ${alpha(theme.palette.divider, 0.4)}` : 'none',
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 20 }}>
                                  <CircleIcon sx={{ fontSize: 6, color: `${section.color}.main` }} />
                                </ListItemIcon>
                                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                  {item}
                                </Typography>
                              </ListItem>
                            ))}
                          </List>
                        ) : (
                          <Typography variant="body2" color="text.secondary" fontSize="0.82rem" fontStyle="italic">
                            None recorded
                          </Typography>
                        )
                      ) : hasData ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                          {section.data.map((item, i) => (
                            <Chip
                              key={i}
                              label={item}
                              size="small"
                              color={section.color}
                              variant="outlined"
                              sx={{ height: 26 }}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" fontSize="0.82rem" fontStyle="italic">
                          None recorded
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Box>

        </Stack>
      </Container>

      <CreateNewTask
        open={openTaskModal}
        onClose={() => setOpenTaskModal(false)}
        leadId={parseInt(callData?.lead?.id)}
        asset_id={parseInt(callId)}
        onSave={async () => setOpenTaskModal(false)}
        lead={callData?.lead || null}
        source="call"
      />
    </Box>
  );
};

export default CallInsights;
